// Human sign-in through Firebase Auth (ADR-0005). Pages use the AuthService interface only.
import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  connectAuthEmulator,
  createUserWithEmailAndPassword,
  getAuth,
  GoogleAuthProvider,
  onAuthStateChanged,
  reload,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  type Auth,
} from "firebase/auth";
import type { ConsoleConfig } from "../config";

/** The signed-in human, as the console needs it. */
export interface AuthUser {
  uid: string;
  email: string | null;
  emailVerified: boolean;
  /** True for email/password accounts. Those need a verified email (docs/tokens.md section 7). */
  passwordAccount: boolean;
  /** Display name from the sign-in provider (Google sets it). Null or absent for most email accounts. */
  displayName?: string | null;
  /** Profile picture URL from the sign-in provider (Google sets it). */
  photoURL?: string | null;
}

/** Sign-in operations used by the console. */
export interface AuthService {
  onChange(cb: (user: AuthUser | null) => void): () => void;
  signInWithGoogle(): Promise<void>;
  signInWithEmail(email: string, password: string): Promise<void>;
  signUpWithEmail(email: string, password: string): Promise<void>;
  sendVerification(): Promise<void>;
  resetPassword(email: string): Promise<void>;
  /** Reloads the user, for example after the user clicks the verification link. */
  refresh(): Promise<AuthUser | null>;
  signOut(): Promise<void>;
  /** A current Firebase ID token. Firebase refreshes it when it is near expiry. */
  getIdToken(forceRefresh?: boolean): Promise<string>;
}

function toUser(auth: Auth): AuthUser | null {
  const u = auth.currentUser;
  if (!u) return null;
  return {
    uid: u.uid,
    email: u.email,
    emailVerified: u.emailVerified,
    passwordAccount: u.providerData.some((p) => p.providerId === "password"),
    displayName: u.displayName,
    photoURL: u.photoURL,
  };
}

/** Creates the Firebase Auth service. Connects to the emulator when the config names one. */
export function createFirebaseAuth(cfg: ConsoleConfig, app?: FirebaseApp): AuthService {
  const firebaseApp = app ?? initializeApp(cfg.firebase);
  const auth = getAuth(firebaseApp);
  if (cfg.authEmulatorUrl) connectAuthEmulator(auth, cfg.authEmulatorUrl, { disableWarnings: true });

  return {
    onChange: (cb) => onAuthStateChanged(auth, () => cb(toUser(auth))),
    signInWithGoogle: async () => {
      await signInWithPopup(auth, new GoogleAuthProvider());
    },
    signInWithEmail: async (email, password) => {
      await signInWithEmailAndPassword(auth, email, password);
    },
    signUpWithEmail: async (email, password) => {
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      await sendEmailVerification(cred.user);
    },
    sendVerification: async () => {
      if (auth.currentUser) await sendEmailVerification(auth.currentUser);
    },
    resetPassword: (email) => sendPasswordResetEmail(auth, email),
    refresh: async () => {
      if (auth.currentUser) {
        await reload(auth.currentUser);
        // A new ID token carries the new email_verified claim.
        await auth.currentUser.getIdToken(true);
      }
      return toUser(auth);
    },
    signOut: () => signOut(auth),
    getIdToken: async (forceRefresh = false) => {
      if (!auth.currentUser) throw new Error("not signed in");
      return auth.currentUser.getIdToken(forceRefresh);
    },
  };
}

/** Maps a Firebase Auth error to a short message for the user. */
export function authErrorMessage(err: unknown): string {
  const code = (err as { code?: string } | null)?.code ?? "";
  switch (code) {
    case "auth/invalid-credential":
    case "auth/wrong-password":
    case "auth/user-not-found":
    case "auth/invalid-email":
      return "The email or password is not correct.";
    case "auth/email-already-in-use":
      return "An account with this email exists. Sign in instead.";
    case "auth/weak-password":
      return "Use a password with at least 8 characters.";
    case "auth/popup-closed-by-user":
    case "auth/cancelled-popup-request":
      return "Sign-in was cancelled.";
    case "auth/too-many-requests":
      return "Too many attempts. Wait a few minutes and try again.";
    case "auth/network-request-failed":
      return "Cannot reach the sign-in service. Check your connection.";
    default:
      return "Sign-in failed. Try again.";
  }
}
